import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Input, InputNumber, Modal, Select, Table, Tag, message } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { adjustMembership, getMemberships } from '../../api/memberships';
import { ADMIN_ROLES } from '../../config/adminAccess';
import { useAuth } from '../../store/authContext';

const membershipTypeMap = {
  FREE: { label: '普通', color: 'default' }, MONTHLY: { label: '月卡', color: 'blue' }, YEARLY: { label: '年卡', color: 'gold' },
};

const Memberships = () => {
  const { admin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [typeFilter, setTypeFilter] = useState('');
  const [adjustVisible, setAdjustVisible] = useState(false);
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [adjustUserId, setAdjustUserId] = useState(null);
  const [adjustForm] = Form.useForm();
  const currentPage = pagination.current;
  const currentPageSize = pagination.pageSize;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getMemberships({ page: currentPage, size: currentPageSize, membershipType: typeFilter || undefined, status: 1 });
      setData(result.list);
      setPagination((current) => ({ ...current, total: result.total }));
    } catch (error) {
      console.error('Failed to fetch memberships:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, currentPageSize, typeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdjust = (userId) => {
    setAdjustUserId(userId);
    adjustForm.resetFields();
    setAdjustVisible(true);
  };

  const handleAdjustSubmit = async () => {
    try {
      const values = await adjustForm.validateFields();
      setAdjustLoading(true);
      await adjustMembership(adjustUserId, values);
      message.success('调整成功');
      setAdjustVisible(false);
      fetchData();
    } catch (error) {
      console.error('Failed to adjust membership:', error);
    } finally {
      setAdjustLoading(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '用户ID', dataIndex: 'userId', width: 80 },
    { title: '会员类型', dataIndex: 'membershipType', render: (type) => { const item = membershipTypeMap[type] || { label: type, color: 'default' }; return <Tag color={item.color}>{item.label}</Tag>; } },
    { title: '开通时返利比例', dataIndex: 'rebateRate', render: (rate) => `${(rate * 100).toFixed(0)}%` },
    { title: '购买金额', dataIndex: 'purchaseAmount', render: (value) => value ? `¥${value.toFixed(2)}` : '-' },
    { title: '开始时间', dataIndex: 'startTime', render: (value) => value ? dayjs(value).format('YYYY-MM-DD') : '-' },
    { title: '到期时间', dataIndex: 'expireTime', render: (value) => value ? dayjs(value).format('YYYY-MM-DD') : '-' },
    { title: '状态', dataIndex: 'status', render: (status, record) => { const active = status === 1 && record.expireTime && dayjs(record.expireTime).isAfter(dayjs()); return <Tag color={active ? 'green' : 'red'}>{active ? '有效' : '已过期'}</Tag>; } },
    { title: '操作', render: (_, record) => <Button size="small" onClick={() => handleAdjust(record.userId)}>调整会员</Button> },
  ];

  return (
    <div>
      <h2>会员管理</h2>
      {admin?.role === ADMIN_ROLES.SUPER_ADMIN && (
        <Alert showIcon type="info" icon={<SettingOutlined />} message="会员策略已迁移到系统配置" description="套餐价格、返利比例、AI 导购和价格监控权益统一在系统配置中维护。" action={<Link to="/admin/system-config#membership">前往系统配置</Link>} style={{ marginBottom: 20 }} />
      )}
      <div style={{ marginBottom: 16 }}>
        <Select placeholder="筛选会员类型" allowClear style={{ width: 150 }} value={typeFilter || undefined} onChange={(value) => { setTypeFilter(value || ''); setPagination((current) => ({ ...current, current: 1 })); }} options={[{ value: 'FREE', label: '普通' }, { value: 'MONTHLY', label: '月卡' }, { value: 'YEARLY', label: '年卡' }]} />
      </div>
      <Table columns={columns} dataSource={data} rowKey="id" loading={loading} pagination={{ ...pagination, showSizeChanger: true, showTotal: (total) => `共 ${total} 条`, onChange: (page, pageSize) => setPagination({ current: page, pageSize, total: pagination.total }) }} />
      <Modal title="调整会员" open={adjustVisible} onCancel={() => setAdjustVisible(false)} onOk={handleAdjustSubmit} confirmLoading={adjustLoading}>
        <Form form={adjustForm} layout="vertical">
          <Form.Item name="membershipType" label="会员类型" rules={[{ required: true, message: '请选择会员类型' }]}><Select options={[{ value: 'FREE', label: '普通' }, { value: 'MONTHLY', label: '月卡' }, { value: 'YEARLY', label: '年卡' }]} /></Form.Item>
          <Form.Item name="extendDays" label="延长天数 (可选)"><InputNumber min={0} placeholder="额外延长的天数" style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="reason" label="调整原因"><Input.TextArea rows={2} placeholder="请输入调整原因" /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Memberships;
